import Component from "@ember/component"
import EmberObject, { computed } from "@ember/object"
import { inject as service } from "@ember/service"
import { isNone } from "@ember/utils"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import type PhantomCardStacks from "cc-frontend/services/phantom-card-stacks"
import _ from "lodash"
import { concat, keyBy, map, min, reduce } from "lodash-es"

export default class Outline extends Component {
  //args
  planbook!: Planbook
  course!: Course
  rotationCalendar!: RotationCalendar
  startDate!: string
  endDate!: string

  // state
  _cachedDatesMap = {}
  dateIdMap = {}

  @service phantomCardStacks!: PhantomCardStacks

  @computed(
    "planbook.attributes.calendar.version",
    "course.attributes.calendar.version",
    "rotationCalendar.attributes.version",
    "startDate",
    "endDate"
  )
  get dates() {
    let units = this.course.attributes.calendar.units

    let rotationCalendarDateMap = keyBy(
      this.rotationCalendar.attributes.dates,
      (date) => date.attributes.dateString
    )

    let courseDateMap = reduce(
      this.course.attributes.calendar.dates,
      (acc, date) => {
        if (date.type === "course-date-custom") acc[date.attributes.dateString] = date.attributes
        return acc
      },
      {}
    )
    let schoolDays = this.rotationCalendar.attributes.schoolDays

    let firstDateOfSchoolYear = min(
      map(this.rotationCalendar.attributes.semesters, (semester) => semester.startDate)
    )
    let lastDateOfSchoolYear = min(
      map(this.rotationCalendar.attributes.semesters, (semester) => semester.endDate)
    )

    let courseDatesOff = this.course.attributes.calendar.datesOff
    let planbookDatesOff = this.planbook.attributes.calendar.datesOff
    let datesOffDictionary = reduce(
      concat(courseDatesOff, planbookDatesOff),
      (acc, dateOff) => {
        acc[dateOff] = true
        return acc
      },
      {}
    )

    let minDate
    let maxDate
    if (this.startDate && this.endDate) {
      minDate = this.startDate
      maxDate = this.endDate
    } else {
      minDate = firstDateOfSchoolYear
      maxDate = lastDateOfSchoolYear
    }

    let list = _.chain(dateFns.eachDay(minDate, maxDate))
      .filter((date) => _.includes(schoolDays, dateFns.getDay(date)))
      .flatMap((date) => {
        let dateString = dateFns.format(date, "YYYY-MM-DD")
        let rotationDate = rotationCalendarDateMap[dateString]
        let courseDate = courseDateMap[dateString] || {}
        let foundUnits = map(courseDate.unitStart, (id) => {
          return Object.assign({}, _.find(units, { id: id }), {
            isUnit: true,
            // Key by id in OutlineAndDetail::List.
            key: `unit:${id}`,
          })
        })

        let isOff =
          rotationDate === undefined
            ? false
            : datesOffDictionary[
                `${rotationDate.attributes.semesterId}:${rotationDate.attributes.rotationId}`
              ] || datesOffDictionary[dateString]

        // We cache the ids so that we don't rerender all the dates as soon as you edit a date
        let id
        if (isNone(courseDate.cardStackId)) {
          this.dateIdMap[dateString] = this.phantomCardStacks.findCardStackIdForDate(
            this.course.id,
            dateString
          )
          id = this.dateIdMap[dateString]
        } else {
          id = courseDate.cardStackId
        }
        if (isOff !== true || courseDate.isForcedOn === true) {
          foundUnits.push({
            id: id,
            // Key by dateString in OutlineAndDetail::List.
            key: `date:${dateString}`,
            // @ts-ignore
            dateString: dateString,
            // @ts-ignore
            isBeforeSchoolYear: dateString <= firstDateOfSchoolYear,
            // @ts-ignore
            isAfterSchoolYear: dateString >= lastDateOfSchoolYear,
            semesterId: rotationDate ? rotationDate.attributes.semesterId : null,
            rotationId: rotationDate ? rotationDate.attributes.rotationId : null,
            isNewLesson: isNone(courseDate.cardStackId),
            cardStackId: id,
            unitStart: courseDate.unitStart || [],
            unitEnd: courseDate.unitEnd || [],
            unitIds: courseDate.unitIds || [],
          })
        }
        return foundUnits
      })
      .value()

    return map(list, (el) => {
      if (this._cachedDatesMap[el.key]) {
        this._cachedDatesMap[el.key].setProperties(el)
      } else {
        this._cachedDatesMap[el.key] = EmberObject.create(el)
      }
      return this._cachedDatesMap[el.key]
    })
  }
}
